import Box from "@mui/material/Box";
import { useState, useEffect } from "react";
import { Typography, Stack, Card, CardContent } from "@mui/material";
import { motion } from "framer-motion";

function WalletRecharge({
    getCardList = [],    // array of cards: [{ pre_transaction_id, masked_creditcard_no_new, card_type, background? }, ...]
    cardReq,
    setCardReq,
}) {
    const userName = JSON.parse(sessionStorage.getItem("userName") || "{}");
    const [activeCard, setActiveCard] = useState(null);
    const [cards, setCards] = useState([]);
    const [selectedMode, setSelectedMode] = useState(4); // 4 = new, 3 = saved

    // Initialize cards list from API
    useEffect(() => {
        if (getCardList?.length) {
            setCards(getCardList);
            setActiveCard(getCardList[0] || null);
        } else {
            setCards([]);
            setActiveCard(null);
        }
    }, [getCardList]);

    const selectNewCard = () => {
        setSelectedMode(4);
        setActiveCard(null);
        setCardReq((prev) => ({
            ...prev,
            paymentOption: "4",
            transRef: null,
        }));
    };

    const selectSavedCard = (card) => {
        if (!card) return;
        setSelectedMode(3);
        setActiveCard(card);
        setCardReq((prev) => ({
            ...prev,
            paymentOption: "3",
            transRef: card.pre_transaction_id,
        }));
    };

    const PaymentOption = ({ label, img, isActive, onClick }) => (
        <Card
            onClick={onClick}
            sx={{
                flex: 1,
                border: isActive ? "2px solid #009688" : "1px solid #ddd",
                borderRadius: 3,
                cursor: "pointer",
                boxShadow: "none",
                transition: "all 0.2s ease-in-out",
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1,
                    py: 2,
                }}
            >
                <img
                    src={img}
                    alt={label}
                    style={{
                        width: 46,
                        height: 46,
                        filter: isActive ? "brightness(100%)" : "grayscale(100%)",
                    }}
                />
                <Typography variant="body2" fontWeight={500}>
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );

    // Bring clicked card to front in the stack
    const handleClickStackCard = (id) => {
        const clicked = cards.find((c) => c.pre_transaction_id === id);
        if (!clicked) return;
        setCards([
            clicked,
            ...cards.filter((c) => c.pre_transaction_id !== id),
        ]);
        selectSavedCard(clicked);
    };

    const hasSavedCards = cards.length > 0;
    const isSavedMode = selectedMode === 3;

    return (
        <Box
            sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                maxWidth: 700,
                width: "100%",
                mx: "auto",
            }}
        >
            <Typography
                variant="subtitle2"
                display="flex"
                justifyContent="start"
                mb={1}
                fontWeight={600}
            >
                Payment Method
            </Typography>

            {/* Payment options */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={2}
                width="100%"
            >
                {hasSavedCards && (
                    <PaymentOption
                        label="Saved Card"
                        img="/img/payment/saved.svg"
                        isActive={isSavedMode}
                        onClick={() => selectSavedCard(activeCard || cards[0])}
                    />
                )}

                <PaymentOption
                    label="New Card"
                    img="/img/payment/newcard.svg"
                    isActive={!hasSavedCards ? true : !isSavedMode}
                    onClick={selectNewCard}
                />
            </Stack>

            {/* Saved card details (visible whenever Saved Card is selected) */}
            {hasSavedCards && isSavedMode && activeCard && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column-reverse", // always stacked
                        gap: { xs: 3, md: 4 },
                        alignItems: "stretch",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        p: { xs: 1, sm: 2 },
                        borderRadius: 2,
                    }}
                >
                    {/* Details */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                        }}
                    >
                        <Box>
                            <Typography
                                fontWeight={600}
                                mb={0.5}
                                display="flex"
                                justifyContent="start"
                            >
                                Name on card
                            </Typography>
                            <Box
                                sx={{
                                    background: "#f3f5f4",
                                    borderRadius: "30px",
                                    px: 2,
                                    py: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: 500,
                                }}
                            >
                                {userName ?? "Passenger Name"}
                            </Box>
                        </Box>

                        <Box>
                            <Typography
                                fontWeight={600}
                                mb={0.5}
                                display="flex"
                                justifyContent="start"
                            >
                                Card Details
                            </Typography>
                            <Box
                                sx={{
                                    background: "#f3f5f4",
                                    borderRadius: "30px",
                                    px: 2,
                                    py: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: 500,
                                }}
                            >
                                xxxx xxxx xxxx {activeCard?.masked_creditcard_no_new ?? activeCard?.masked_creditcard_no}
                            </Box>
                        </Box>
                    </Box>

                    {/* Card preview */}
                    {cards.length === 1 ? (
                        <Card
                            sx={{
                                borderRadius: "16px",
                                background: "linear-gradient(135deg, #27b0b8, #007ea7)",
                                color: "#fff",
                                width: "100%",
                                maxWidth: 360,
                                height: 200,
                                position: "relative",
                                boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                                overflow: "hidden",
                                mx: { xs: "auto", md: 0 },
                            }}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    height: "100%",
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src="/img/chip.svg"
                                            alt="Card chip"
                                            sx={{
                                                width: 36,
                                                height: 26,
                                                borderRadius: "4px",
                                                objectFit: "cover",
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            color="rgba(255,255,255,0.8)"
                                        >
                                            {activeCard?.card_type}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        display="flex"
                                        justifyContent="start"
                                        color="rgba(255,255,255,0.8)"
                                        letterSpacing={3}
                                    >
                                        **** **** **** {activeCard?.masked_creditcard_no_new ?? activeCard?.masked_creditcard_no}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-end",
                                        mt: 2,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="rgba(255,255,255,0.8)"
                                        >
                                            Card Holder name
                                        </Typography>
                                        <Typography fontWeight={500}>
                                            {userName ?? "Passenger Name"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="rgba(255,255,255,0.8)"
                                        >
                                            Expiry Date
                                        </Typography>
                                        <Typography fontWeight={500}>02/30</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                maxWidth: 320,
                                height: 220,
                                mx: "auto",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: 200,
                                    margin: "auto",
                                    perspective: 1000,
                                    background: "#000",
                                    borderRadius: "16px 16px 30px 30px",
                                }}
                            >
                                {cards.map((card, index) => (
                                    <motion.div
                                        key={card.pre_transaction_id}
                                        layout
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25,
                                        }}
                                        onClick={() =>
                                            handleClickStackCard(card.pre_transaction_id)
                                        }
                                        style={{
                                            position: "absolute",
                                            bottom: index === 0 ? 60 : 60 + index * 25,
                                            left: 0,
                                            width: "100%",
                                            height: 80,
                                            cursor: "pointer",
                                            zIndex: cards.length - index,
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "100%",
                                                maxWidth: 260,
                                                height: "100%",
                                                borderRadius: "16px",
                                                background: card.background || "#27b0b8",
                                                color: "#fff",
                                                boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent: "space-between",
                                                px: 2,
                                                py: 1,
                                                border: "1px solid #000",
                                                transition: "transform 0.3s",
                                                "&:hover": { transform: "scale(1.02)" },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="/img/chip.svg"
                                                alt="Card chip"
                                                sx={{
                                                    width: 26,
                                                    height: 16,
                                                    borderRadius: "4px",
                                                    objectFit: "cover",
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    textAlign: "right",
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                }}
                                            >
                                                {card.card_type}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                ))}
                            </Box>

                            {/* Pocket / Holder */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: -10,
                                    width: "100%",
                                    height: 120,
                                    backgroundImage: "url('/img/FrontPart.svg')",
                                    backgroundSize: "cover",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                    display: "flex",
                                    alignItems: "flex-end",
                                    justifyContent: "center",
                                    paddingBottom: 2,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ color: "white", fontWeight: 600 }}
                                >
                                    {userName ?? "Passenger Name"}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default WalletRecharge;
